import { useEffect, useState } from 'react';
import { isIosDevice } from '@/lib/captureLimits';

// Hankeleping Lisa 1, "Süsteemi miinimumnõuded".
const MIN_IOS = 17;
const MIN_ANDROID = 13;

// Chromium's reduced UA freezes every Android phone at this.
const FROZEN_ANDROID_UA = 'Android 10; K)';

function major(version: string | undefined): number | undefined {
  const value = version === undefined ? NaN : parseInt(version, 10);
  return Number.isNaN(value) ? undefined : value;
}

/**
 * True only when the OS is known to be older than the contract minimum; an
 * unknown version is not warned about. Every iOS browser carries "OS 16_7";
 * iPadOS desktop mode has only Safari's Version/. Chromium on Android needs
 * `androidPlatformVersion` from userAgentData, since its UA is frozen.
 */
export function isBelowMinimumOs(userAgent: string, maxTouchPoints: number, androidPlatformVersion?: string): boolean {
  if (isIosDevice(userAgent, maxTouchPoints)) {
    const ios = major(userAgent.match(/OS (\d+)_/)?.[1] ?? userAgent.match(/Version\/(\d+)/)?.[1]);
    return ios !== undefined && ios < MIN_IOS;
  }
  if (!/Android/.test(userAgent)) return false;

  const fromUa = userAgent.includes(FROZEN_ANDROID_UA) ? undefined : userAgent.match(/Android (\d+)/)?.[1];
  const android = major(androidPlatformVersion ?? fromUa);
  return android !== undefined && android < MIN_ANDROID;
}

type UserAgentData = { getHighEntropyValues(hints: string[]): Promise<{ platformVersion?: string }> };

export function useBelowMinimumOs(): boolean {
  const userAgent = navigator.userAgent;
  const maxTouchPoints = navigator.maxTouchPoints ?? 0;
  const [below, setBelow] = useState(() => isBelowMinimumOs(userAgent, maxTouchPoints));

  useEffect(() => {
    const uaData = (navigator as Navigator & { userAgentData?: UserAgentData }).userAgentData;
    if (!uaData || !/Android/.test(userAgent)) return;
    uaData
      .getHighEntropyValues(['platformVersion'])
      .then(({ platformVersion }) => setBelow(isBelowMinimumOs(userAgent, maxTouchPoints, platformVersion)))
      .catch(() => {});
  }, [userAgent, maxTouchPoints]);

  return below;
}
